import React, { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import UndoIcon from '@mui/icons-material/Undo';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { withdrawApproval } from '../../api/approvalApi';
import { useToast } from '../../context/ToastContext';
import { useApproval } from '../../context/ApprovalContext';

export default function WithdrawButton({ approvalId, onWithdrawn, variant = 'withdraw' }) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { isMySubmission, refreshMySubmissions } = useApproval();
  const [, setRechecked] = useState(0);
  const isDismiss = variant === 'dismiss';
  const isMine = isMySubmission(approvalId);

  // The context loads "my submissions" once per page load, so a proposal the server created afterwards
  // (e.g. an ad edit) is not in it yet. Ask the server once for an id we don't recognise and redraw;
  // if the approval belongs to someone else it stays hidden, and the unchanged deps prevent a loop.
  useEffect(() => {
    if (isMine || approvalId == null || typeof refreshMySubmissions !== 'function') return undefined;
    let alive = true;
    Promise.resolve(refreshMySubmissions()).then(() => { if (alive) setRechecked((n) => n + 1); });
    return () => { alive = false; };
  }, [approvalId, isMine, refreshMySubmissions]);

  if (!isMine) return null;

  const handleWithdraw = async () => {
    if (!approvalId) return;
    setConfirmOpen(false);
    setLoading(true);
    try {
      await withdrawApproval(approvalId);
      toast.success(isDismiss
        ? 'Rejected proposal dismissed. You can now edit or delete this content.'
        : 'Submission withdrawn. You can now edit the content.');
      onWithdrawn?.();
    } catch {
      // error toast handled by apiConfig interceptor
    } finally {
      setLoading(false);
    }
  };

  const withdrawSx = {
    borderColor: '#f59e0b',
    color: '#92400e',
    bgcolor: '#fffbeb',
    borderRadius: '8px',
    px: 1.5,
    py: 0.6,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#fef3c7', borderColor: '#d97706', boxShadow: 'none' },
    '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#9ca3af', bgcolor: 'transparent' },
  };

  const dismissSx = {
    borderColor: '#dc2626',
    color: '#991b1b',
    bgcolor: '#fff1f2',
    borderRadius: '8px',
    px: 1.5,
    py: 0.6,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
    whiteSpace: 'nowrap',
    boxShadow: 'none',
    '&:hover': { bgcolor: '#fee2e2', borderColor: '#b91c1c', boxShadow: 'none' },
    '&.Mui-disabled': { borderColor: '#e5e7eb', color: '#9ca3af', bgcolor: 'transparent' },
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        startIcon={isDismiss
          ? <HighlightOffIcon sx={{ fontSize: 13 }} />
          : <UndoIcon sx={{ fontSize: 13 }} />}
        onClick={(e) => { e.currentTarget.blur(); setConfirmOpen(true); }}
        disabled={loading || !approvalId}
        sx={isDismiss ? dismissSx : withdrawSx}
      >
        {loading
          ? (isDismiss ? 'Dismissing…' : 'Withdrawing…')
          : (isDismiss ? 'Dismiss' : 'Withdraw')}
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {isDismiss ? 'Dismiss Rejected Proposal?' : 'Withdraw Submission?'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {isDismiss
              ? 'This will clear the rejected proposal. The original content remains unchanged and you can edit or submit a new proposal freely.'
              : 'This will return the content to draft status. The assigned approver will no longer see it in their inbox.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={isDismiss ? 'error' : 'warning'}
            onClick={handleWithdraw}
          >
            {isDismiss ? 'Dismiss' : 'Withdraw'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
